import { formatPersonName } from '@/utils/personName';
/**
 * PdwShell — canonical Person Add Wizard shell.
 *
 * Owns the linear-stage state machine in ONE persistent modal. Resolves the
 * disjointed-breadcrumb pattern called out by `feedback_pdw_breadcrumb_
 * disjointed_must_match_spec_20260513`.
 *
 * Person-Modal CONSOLIDATION (Bill 2026-06-11, LOCKED mockup v6 —
 * docs/operations/person-modal-inventory/06-INDIVIDUAL-SURFACE-CLEANUP-MOCKUP.html):
 * the Personal flow is now bucket → ONE consolidated body. The old Stage-2
 * cohort screen ('cohort-personal' / CitizenshipCohortGate) and the separate
 * docs-grid stage ('docs-grid' / hosted PdwStage4DocsGrid) COLLAPSED into the
 * consolidated IndividualReviewBody, which renders: Citizenship block (top) →
 * Documents-to-capture tile grid → CIP → Profile/SSN-TIN → Work-School →
 * Address & Contact → Verify, with a jump-scroll tab strip in that order.
 *
 *   Stage  enum value         notes
 *   ─────  ──────────────     ─────────────────
 *   1      'bucket'           bucket grid (Personal pick → 'review' directly)
 *   3      'subtype-confirm'  non-Personal subtype cards — UNTOUCHED by the
 *                                 consolidation; Continue fires
 *                                 onNonPersonalDispatch + closes shell.
 *   9      'review'           the consolidated Personal body. Add-new opens
 *                                 with citizenship UNSET (D2 — tiles appear
 *                                 after selection); review-entry (existing
 *                                 CIF) opens hydrated on the same surface.
 *
 * Used by:
 *   - PeopleStep.tsx — the "+ New Person" button opens this shell; Personal
 *     review-add / Edit enter via `entryPerson` (review-entry mode).
 *
 * NO LEFT RAIL anywhere — per
 * `feedback_mockup_left_rail_is_nav_not_design_20260514`.
 *
 * NO 3-stage progress ribbon — per
 * `feedback_continue_return_navigation_convention_20260511`, the only
 * forward/back affordance is the canonical Return/Continue dyad.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CustomerType } from '@retail-ops/shared-types/enums';
import { Banner, ModalShell, useConfirm } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { EntitySubtypeIcon } from '@/icons/EntitySubtypeIcon';
import { entityBucketIconUrl } from '@/icons/flat-library';
import { ENTITY_GROUPS, type EntityGroup } from '../../entityGroups';
import type { RaoPerson, RaoPersonRole } from '../../types';
import { PdwFooterDyad } from './shared/PdwFooterDyad';
import {
  IndividualReviewBody,
  type IndividualReviewBodyMeta,
} from './individual/IndividualReviewBody';
// OFAC save-time auto-fire (Bill 2026-07-23) — same headless core the Verify
// section's "Run" button uses, so results/persistence are byte-identical.
import {
  buildOfacExtraPatch,
  buildOfacScreenName,
  hasFreshOfacScan,
  hasFreshSatisfactoryOfac,
  performOfacScan,
  readOfacStaleDays,
} from './shared/ofacAutoScan';
import { normalizeOfacStatus } from './shared/ofacStatus';
import { rolloutAllows, rolloutHidesDisabled, SETTING_DISABLED_DISPLAY, SETTING_ENABLED_ENTITY_GROUPS } from '../../../../utils/rolloutScope';

// ---------------------------------------------------------------------------
// Stage state machine
// ---------------------------------------------------------------------------

/**
 * PdwShellStage — explicit linear-stage enum. The 'cohort-personal' +
 * 'docs-grid' stages were REMOVED by the 2026-06-11 consolidation (both
 * surfaces live inside the consolidated IndividualReviewBody now).
 */
export type PdwShellStage =
  | 'bucket'           // Stage 1
  | 'subtype-confirm'  // Stage 3b-3g (non-Personal subtype cards)
  | 'review';          // the consolidated Personal body

/** Stage-order index for PdwFooterDyad's currentStep/totalSteps math. */
const STAGE_ORDER: ReadonlyArray<PdwShellStage> = [
  'bucket',
  'subtype-confirm',
  'review',
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Non-Personal dispatch payload — parent opens the per-category legacy dialog. */
export interface NonPersonalPick {
  customerType: CustomerType;
}

export interface PdwShellProps {
  open: boolean;
  onClose: () => void;
  /** Required for the body's DocumentSlotsSection. */
  cartId: string;
  /** Set of CIF IDs already on the cart (dup detection in review-add mode). */
  cartCifIds?: ReadonlyArray<string>;
  /** Officer code for document upload metadata. Defaults to 'UNKNOWN'. */
  officerCode?: string;
  /** Cart status tier (Production / UAT / Sandbox) — filters subtypes. */
  cartMode?: string;
  /** Role assigned to the new person draft (currently always 'primary'). */
  defaultRole?: RaoPersonRole;
  /**
   * Called when a non-Personal bucket completes Stage 3 confirm. Parent
   * closes the shell + opens the legacy PersonDialog dispatcher for the
   * per-category dialog (Business / Trust / Estate / Fiduciary / Gov /
   * SpecialPurpose). Those buckets get migrated into the shell in 0071+.
   */
  onNonPersonalDispatch: (pick: NonPersonalPick) => void;
  /**
   * Called when the Personal flow reaches Stage 9 terminal Save. Parent
   * adds the draft to the cart's peopleJson + closes the shell.
   */
  onSave: (person: RaoPerson) => void;
  /**
   * Slot-4 T1 — review-ENTRY mode. When supplied (with `open`), the shell
   * skips Stages 1-4 and opens directly at the Stage-9 review form seeded with
   * this existing person. Used to converge the Personal-cohort review-add
   * (existing-CIF search hit) + Edit paths onto PdwShell instead of the bare
   * legacy IndividualPersonDialog. Non-Personal entities keep their own
   * EntityConvergedShell dialog (their bodies aren't hosted in the shell yet).
   */
  entryPerson?: RaoPerson;
  /** Review-entry mode flavor — drives tab order (D2) + terminal save label. */
  entryMode?: 'review-add' | 'edit';
  /**
   * E1 (Bill 2026-06-03): forwarded to IndividualReviewBody so the host can run
   * the silent dup-CIF check when a complete SSN is entered on a fresh-new
   * person (early), not just at the terminal Add.
   */
  /** markNoTinCleared (Bill 2026-10-14): the host calls it when Core was searched by name + DOB
   *  for this draft and nothing was found, or the officer dismissed the match — the no-TIN proof. */
  onEarlyDupCheck?: (draft: RaoPerson, markNoTinCleared?: (key: string) => void) => void;
}

// ---------------------------------------------------------------------------
// Mode filter for subtype gating
// ---------------------------------------------------------------------------

/**
 * Subtype admissibility per cart mode (Production / UAT-Live / Test-Live /
 * Sandbox). Production carts only see Production-tier subtypes; UAT-Live
 * sees Production + UAT; Test/Sandbox sees everything. Default = Production.
 */
function allowedForMode(status: string | undefined, cartMode: string | undefined): boolean {
  const s = status ?? 'Production';
  if (cartMode === 'Sandbox' || cartMode === 'Test-Live') return true;
  if (cartMode === 'UAT-Live') return s === 'Production' || s === 'UAT';
  return s === 'Production';
}

/**
 * CustomerType set that is never FSR-selectable (e.g., ICS Business / ICS
 * Personal in Special Purpose — loaded by Finance via Core Director directly,
 * not through this picker). Clicking one in the subtype view surfaces an inline
 * explainer instead of selecting.
 */
const NOT_FSR_INITIATED: ReadonlySet<string> = new Set(['ICS_Business', 'ICS_Personal']);

/**
 * Whether a subtype can actually be PICKED in this cart mode. A subtype is
 * selectable only when it is admissible for the mode (its EntityRules Status
 * tier passes `allowedForMode`) AND it is an FSR-initiated flow.
 *
 * Everything else is rendered as a DISABLED scaffold card (Bill 2026-06-13) so
 * the full taxonomy is visible — UAT/"Pilot" + Future subtypes (UTMA, QIT,
 * SelfSettledSNT, IOLTA, USDA Grant, Medical Set-Aside, Unincorporated
 * Association, Donation, Political Committee, Public Fund, the fiduciaries) and
 * the Finance-only ICS pair show greyed/"coming soon" instead of vanishing.
 * The tier comes from config (EntityRules.Status), so Admin can promote a
 * subtype to Production and it lights up with no code change.
 */
function isSubtypeSelectable(
  type: string,
  status: string | undefined,
  cartMode: string | undefined,
): boolean {
  if (NOT_FSR_INITIATED.has(type)) return false;
  return allowedForMode(status, cartMode);
}

// ---------------------------------------------------------------------------
// Blank-person factory — the shell owns the Personal draft from the bucket
// pick through the consolidated body's terminal save.
// ---------------------------------------------------------------------------

/**
 * Consolidation (Bill 2026-06-11, D2): add-new opens with citizenship UNSET —
 * the cohort is picked inside the consolidated body's Citizenship block,
 * which also derives customerType ('NonResidentAlien' vs 'Individual') +
 * tinCode ('Forn' vs 'S') on selection (syncCustomerType). The draft seeds
 * as a plain Individual until then; the document tiles appear only after the
 * citizenship selection.
 */
function blankPersonalDraft(role: RaoPersonRole): RaoPerson {
  return {
    role,
    mode: 'new',
    firstName: '',
    lastName: '',
    fullName: '',
    customerType: 'Individual',
    tinCode: 'S',
    cipStatus: 'pending',
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PdwShell({
  open,
  onClose,
  cartId,
  cartCifIds,
  officerCode,
  cartMode,
  defaultRole = 'primary',
  onNonPersonalDispatch,
  onSave,
  entryPerson,
  entryMode,
  onEarlyDupCheck,
}: PdwShellProps): JSX.Element {
  const { config } = useConfig();
  const client = useApiClient();
  // BACKLOG-1297 — in-app discard confirm (native dialogs no-op in Teams).
  const confirm = useConfirm();

  // OFAC auto-fire (Bill 2026-07-23): staleness window shared with the server
  // rescreen backstop — SystemSetting risk.ofac.rescreenStaleAfterDays.
  const ofacStaleDays = useMemo(
    () => readOfacStaleDays(config.systemSettings as Record<string, unknown> | undefined),
    [config.systemSettings],
  );
  /** True while the save-time OFAC screen is in flight — Continue is held. */
  const [ofacBusy, setOfacBusy] = useState<boolean>(false);
  /** Save-hold notice (active hit / scan problem) rendered above the body. */
  const [ofacNotice, setOfacNotice] = useState<string | null>(null);

  // Stage 1 selection state
  const [selectedGroup, setSelectedGroup] = useState<EntityGroup | null>(null);
  // Stage 3 selection state (non-Personal) — picked by subtype confirm cards
  const [selectedType, setSelectedType] = useState<CustomerType | null>(null);
  // Consolidated-body draft — seeded on the Personal bucket pick (citizenship
  // UNSET per D2; the body's Citizenship block drives cohort + tiles).
  const [personalDraft, setPersonalDraft] = useState<RaoPerson | null>(null);
  const [reviewMeta, setReviewMeta] = useState<IndividualReviewBodyMeta>({
    hasChanges: false,
    dupInCart: false,
    saveDisabled: false,
    activeTab: 'citizenship',
  });
  const [latestDraft, setLatestDraft] = useState<RaoPerson | null>(null);
  // Explicit current-stage marker.
  const [stage, setStage] = useState<PdwShellStage>('bucket');

  // Slot-4 T1 — review-entry. When the parent opens the shell with an existing
  // person (Personal review-add / Edit), jump straight to Stage 9 seeded with
  // that person. Skips bucket → cohort → docs-grid (those don't apply to an
  // existing CIF — mirrors the SPEC A5.1 reduced path).
  useEffect(() => {
    if (open && entryPerson) {
      setPersonalDraft(entryPerson);
      setLatestDraft(entryPerson);
      setStage('review');
    }
  }, [open, entryPerson]);

  // Subtype-status filter from active EntityRules.
  const statusByType = useMemo(() => {
    const m = new Map<string, 'Production' | 'UAT' | 'Future'>();
    for (const r of (config.entityRules ?? [])) {
      if (!r.isActive) continue;
      m.set(r.customerType, r.status ?? 'Production');
    }
    return m;
  }, [config.entityRules]);

  // Bucket-grid models (Bill 2026-06-13). Every one of the 7 buckets is ALWAYS
  // rendered — we no longer hide buckets whose subtypes are all pilot/future.
  // `selectableCount` counts the subtypes that can actually be picked in this
  // cart mode; a bucket with zero selectable subtypes renders DISABLED ("coming
  // soon") instead of dead-ending the FSR into an all-disabled subtype list.
  // Phase rollout scope (Bill 2026-10-06): a bucket not in rollout.enabledEntityGroups is
  // "coming soon" whatever its subtypes' status — Phase 1 opens Personal only.
  const enabledGroupsRaw = (config.systemSettings as Record<string, unknown> | undefined)?.[SETTING_ENABLED_ENTITY_GROUPS];
  const bucketModels = useMemo(() => {
    return ENTITY_GROUPS.map((g) => {
      const inScope = rolloutAllows(enabledGroupsRaw, g.key);
      const selectableCount = inScope
        ? g.types.filter((t) => isSubtypeSelectable(t.type, statusByType.get(t.type), cartMode)).length
        : 0;
      return { group: g, selectableCount, disabled: selectableCount === 0 };
    });
  }, [statusByType, cartMode, enabledGroupsRaw]);

  // ---------------------------------------------------------------------------
  // Stage transitions
  // ---------------------------------------------------------------------------

  const resetSelection = (): void => {
    setSelectedGroup(null);
    setSelectedType(null);
    setPersonalDraft(null);
    setLatestDraft(null);
    setOfacNotice(null);
    setReviewMeta({
      hasChanges: false,
      dupInCart: false,
      saveDisabled: false,
      activeTab: 'citizenship',
    });
    setStage('bucket');
  };

  const handleClose = (): void => {
    resetSelection();
    onClose();
  };

  const handlePickGroup = (g: EntityGroup): void => {
    setSelectedGroup(g);
    if (g.key === 'personal') {
      // Consolidation (Bill 2026-06-11) — Personal goes STRAIGHT to the
      // consolidated body. Citizenship opens UNSET (D2); the body's
      // Citizenship block derives cohort, customerType and the tile set.
      setSelectedType(g.defaultType);
      const draft = blankPersonalDraft(defaultRole);
      setPersonalDraft(draft);
      setLatestDraft(draft);
      setStage('review');
    } else {
      // Pre-select the bucket's default subtype if it's actually selectable in
      // this cart mode; otherwise the first selectable subtype (so the Continue
      // CTA isn't armed on a disabled scaffold card). A fully-disabled bucket
      // can't reach here — its grid tile is non-clickable.
      const defaultOk = isSubtypeSelectable(
        g.defaultType, statusByType.get(g.defaultType), cartMode,
      );
      const firstSelectable = g.types.find((t) =>
        isSubtypeSelectable(t.type, statusByType.get(t.type), cartMode),
      );
      setSelectedType(defaultOk ? g.defaultType : (firstSelectable?.type ?? null));
      setStage('subtype-confirm');
    }
  };

  const handleReturn = useCallback((): void => {
    // Stage-aware Return. Personal flow back-stack: review → bucket
    // (the cohort + docs stages collapsed into the body). Non-Personal:
    // subtype-confirm → bucket (Stage 3 hands off to legacy dispatch on
    // Continue, so it's not in the back-stack from there).
    switch (stage) {
      case 'subtype-confirm':
        setSelectedGroup(null);
        setSelectedType(null);
        setStage('bucket');
        return;
      case 'review': {
        // Review-entry (existing-CIF / Edit) has no back-stack — Return
        // closes. Fresh-add Review backs to the bucket grid; a dirty draft
        // gets a positive discard confirm first (data-loss guard — the old
        // stepwise back-stack discarded silently only at the very top).
        if (entryPerson) {
          resetSelection();
          onClose();
          return;
        }
        const draft = latestDraft ?? personalDraft;
        const draftDirty =
          !!draft &&
          (reviewMeta.hasChanges ||
            !!draft.citizenshipStatus ||
            !!draft.firstName ||
            !!draft.lastName);
        const discardAndReturn = (): void => {
          setPersonalDraft(null);
          setLatestDraft(null);
          setSelectedGroup(null);
          setSelectedType(null);
          setReviewMeta({
            hasChanges: false,
            dupInCart: false,
            saveDisabled: false,
            activeTab: 'citizenship',
          });
          setStage('bucket');
        };
        if (draftDirty) {
          // BACKLOG-1297 — window.confirm() is a silent no-op inside Teams, so
          // the draft used to vanish with no question asked. In-app confirm.
          void confirm({
            title: 'Discard this person?',
            message: 'You\'ll lose the citizenship, identity, and document capture you\'ve entered.',
            confirmLabel: 'Discard person',
            cancelLabel: 'Keep editing',
            tone: 'danger',
          }).then((ok) => {
            if (ok) discardAndReturn();
          });
          return;
        }
        discardAndReturn();
        return;
      }
      case 'bucket':
      default:
        return; // Already at the top.
    }
  }, [stage, entryPerson, onClose, latestDraft, personalDraft, reviewMeta.hasChanges, confirm]);

  const handleContinue = useCallback((): void => {
    switch (stage) {
      case 'bucket':
        // Bucket-grid is click-to-advance; Continue is disabled here.
        return;

      case 'subtype-confirm': {
        if (!selectedType) return;
        // Non-Personal flow: hand off to legacy PersonDialog dispatcher.
        // Shell closes; parent opens the per-category dialog with a
        // blank person seeded from the picked customerType. Bringing
        // each non-Personal bucket into the shell is 0071+ scope.
        const pick: NonPersonalPick = { customerType: selectedType };
        resetSelection();
        onNonPersonalDispatch(pick);
        return;
      }

      case 'review': {
        if (reviewMeta.saveDisabled || ofacBusy) return;
        const draft = latestDraft ?? personalDraft;
        if (!draft) return;

        // ── OFAC auto-fire at terminal Save (Bill 2026-07-23) ────────────
        // NEW persons only — existing-CIF people are deliberately not
        // screened in the modal (2026-05-21 rule); the server rescreen
        // backstop covers them silently at commit-validate (alpha.354).
        // A satisfactory screen (clear / archived-only / FSR override)
        // within the shared staleness window saves straight through.
        const needsOfac = !draft.cifId && !hasFreshSatisfactoryOfac(draft, ofacStaleDays);
        if (!needsOfac) {
          setOfacNotice(null);
          resetSelection();
          onSave(draft);
          return;
        }

        // A FRESH unresolved hit: don't burn another scan — hold the modal
        // on the normal hit workflow (Verify section) until the FSR
        // overrides with a compensating doc or removes the person.
        const draftExtra = (draft as unknown as { extra?: Record<string, unknown> }).extra;
        const priorStatus = normalizeOfacStatus(draftExtra?.['ofacStatus'] as string | undefined);
        if (priorStatus === 'hit-current' && hasFreshOfacScan(draft, ofacStaleDays)) {
          setOfacNotice(
            'Active OFAC sanctions match on this person — the save is held. '
            + 'Open the Verify section to review the match; override with a '
            + 'compensating document if it is a false positive.',
          );
          return;
        }

        void (async (): Promise<void> => {
          setOfacBusy(true);
          setOfacNotice(null);
          try {
            const name = buildOfacScreenName(draft);
            if (name.length === 0) {
              // Completeness gate requires first+last name, so this is
              // unreachable in practice — hold rather than skip the screen.
              setOfacNotice('Enter the person’s name before saving — the OFAC screen needs a full name.');
              return;
            }
            const outcome = await performOfacScan(client, name, 'Indv', {
              birthDate: draft.dob || undefined,
              cartId: cartId || undefined,
              personKey: draft.id || undefined,
            });
            const nextExtra = buildOfacExtraPatch(
              draftExtra, outcome.status, outcome.matches, outcome.records, outcome.screening,
            );
            const nextDraft = { ...draft, extra: nextExtra } as RaoPerson;
            if (outcome.status === 'clear' || outcome.status === 'archived-only' || outcome.status === 'pending') {
              // Satisfactory → silent save-through (Bill's spec). 'pending' (a
              // list could not be confirmed current) saves through like an
              // outage: the commit gate stays fail-closed and re-screens.
              resetSelection();
              onSave(nextDraft);
              return;
            }
            // hit-current → persist the scan into the draft (the body
            // re-seeds from the person prop, so the Verify section renders
            // the standard hit cards + override workflow) and hold open.
            setPersonalDraft(nextDraft);
            setLatestDraft(nextDraft);
            setOfacNotice(
              'The sanctions screening found a possible match on a government list, so the save is held. '
              + 'Open the Verify section to see the listed party. Risk Management reviews it.',
            );
          } catch {
            // JHA unreachable: never wedge person capture on an outage.
            // Save through with status 'pending' — the Review-screen OFAC
            // gate + the server commit-validate backstop stay fail-closed,
            // and the alpha.354 self-heal re-screens at commit.
            const pendingExtra = buildOfacExtraPatch(draftExtra, 'pending', [], []);
            resetSelection();
            onSave({ ...draft, extra: pendingExtra } as RaoPerson);
          } finally {
            setOfacBusy(false);
          }
        })();
        return;
      }

      default:
        return;
    }
  }, [stage, selectedType, reviewMeta.saveDisabled, latestDraft, personalDraft, onNonPersonalDispatch, onSave, ofacBusy, ofacStaleDays, client]);

  const handleReviewDraftChange = useCallback(
    (draft: RaoPerson, meta: IndividualReviewBodyMeta): void => {
      setLatestDraft(draft);
      setReviewMeta(meta);
      // Any edit (including an FSR override submitted in the Verify section)
      // invalidates the save-hold notice; the next Continue re-evaluates.
      setOfacNotice(null);
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Stage-aware title / footer state
  // ---------------------------------------------------------------------------

  const stageTitle: string = useMemo(() => {
    switch (stage) {
      case 'bucket':
        return 'Add new person — pick entity category';
      case 'subtype-confirm':
        return `Add new person — ${selectedGroup?.label ?? ''} subtype`;
      case 'review': {
        const nm = personalDraft
          ? formatPersonName(personalDraft)
          : '';
        if (entryPerson) {
          // Review-entry (existing CIF) — mode-aware title.
          if (entryMode === 'edit') return nm ? `Edit person — ${nm}` : 'Edit person';
          return nm ? `Review & add — ${nm}` : 'Review & add';
        }
        return nm ? `Add new person — ${nm}` : 'Add new person';
      }
      default:
        return 'Add new person';
    }
  }, [stage, selectedGroup, personalDraft, entryPerson, entryMode]);

  const continueEnabled: boolean = useMemo(() => {
    switch (stage) {
      case 'subtype-confirm':
        return selectedType !== null;
      case 'review':
        // Capture remains a SOFT gate (Bill 2026-06-02): the required/total
        // counter in the body's Documents grid flags what's missing; the
        // cart-commit gate is the real backstop. Continue is also held while
        // the save-time OFAC screen is in flight.
        return !reviewMeta.saveDisabled && !ofacBusy;
      case 'bucket':
      default:
        return false;
    }
  }, [stage, selectedType, reviewMeta.saveDisabled, ofacBusy]);

  const continueLabel: string | undefined = useMemo(() => {
    if (stage === 'review') return undefined; // PdwFooterDyad uses lastStepLabel
    return 'Continue →';
  }, [stage]);

  const lastStepLabel: string = useMemo(() => {
    // Review-entry (existing CIF) commit copy mirrors IndividualPersonDialog's
    // legacy labels; fresh-add stays "Add new" for FSR familiarity.
    if (entryPerson) {
      if (entryMode === 'edit') return 'Save changes →';
      return reviewMeta.hasChanges ? 'Add existing w/ changes →' : 'Add existing →';
    }
    return 'Add new →';
  }, [entryPerson, entryMode, reviewMeta.hasChanges]);

  const continueDisabledReason: string | undefined = useMemo(() => {
    if (stage === 'review' && ofacBusy) return 'Running the OFAC sanctions screen…';
    if (stage === 'review' && reviewMeta.saveDisabledReason) return reviewMeta.saveDisabledReason;
    if (stage === 'subtype-confirm' && !selectedType) return 'Pick a subtype to continue';
    return undefined;
  }, [stage, reviewMeta.saveDisabledReason, selectedType, ofacBusy]);

  // currentStep / totalSteps for PdwFooterDyad's "is-last-stage" math.
  const stageIndex = useMemo(() => {
    const i = STAGE_ORDER.indexOf(stage);
    return i < 0 ? 0 : i;
  }, [stage]);
  const totalStages = STAGE_ORDER.length;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Data-loss guard (Bill 2026-06-02) — "dirty" once the FSR has progressed past
  // the entity picker AND has a draft / edited a field. A stray scrim click or
  // Escape can no longer nuke this; close routes through a positive discard
  // confirm. Scrim close is disabled outright (only a deliberate X / Return
  // acts), and that X confirms when dirty.
  const dirty =
    stage !== 'bucket' &&
    (!!personalDraft || !!latestDraft || reviewMeta.hasChanges);

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      title={stageTitle}
      size="lg"
      disableBackdropClose
      confirmClose
      isDirty={dirty}
      confirmCloseTitle="Discard this person?"
      confirmCloseMessage="You'll lose the citizenship, identity, and document capture you've entered for this person. This can't be undone."
      footer={
        <PdwFooterDyad
          currentStep={stageIndex}
          totalSteps={totalStages}
          onReturn={handleReturn}
          onContinue={handleContinue}
          returnDisabled={stage === 'bucket'}
          continueDisabled={!continueEnabled}
          {...(continueDisabledReason ? { continueDisabledReason } : {})}
          lastStepLabel={lastStepLabel}
          {...(continueLabel ? { continueLabel } : {})}
        />
      }
    >
      {stage === 'bucket' && (
        <BucketGrid
          buckets={rolloutHidesDisabled((config.systemSettings as Record<string, unknown> | undefined)?.[SETTING_DISABLED_DISPLAY])
            ? bucketModels.filter((b) => !b.disabled)
            : bucketModels}
          onPickGroup={handlePickGroup}
        />
      )}

      {stage === 'subtype-confirm' && selectedGroup && selectedGroup.key !== 'personal' && (
        <SubtypeConfirm
          group={selectedGroup}
          statusByType={statusByType}
          cartMode={cartMode}
          selectedType={selectedType}
          onSelectType={setSelectedType}
          onConfirm={handleContinue}
          onBack={handleReturn}
        />
      )}

      {/* The consolidated Personal body — Citizenship block (top) →
          Documents-to-capture grid → CIP → Profile/SSN-TIN → Work-School →
          Address & Contact → Verify. The old cohort-gate + docs-grid stages
          live INSIDE it now (Bill 2026-06-11, LOCKED mockup v6). */}
      {/* OFAC save-hold notice — active hit (or missing name) discovered by
          the save-time auto-fire. Pinned above the body so the FSR sees WHY
          the modal stayed open without scrolling to Verify. */}
      {stage === 'review' && ofacNotice && (
        <div style={{ marginBottom: 'var(--sp-3)' }}>
          <Banner tone="err">{ofacNotice}</Banner>
        </div>
      )}

      {stage === 'review' && personalDraft && (
        <IndividualReviewBody
          person={personalDraft}
          mode={entryPerson ? (entryMode ?? 'edit') : 'new'}
          cartId={cartId}
          {...(cartMode !== undefined ? { cartMode } : {})}
          {...(officerCode !== undefined ? { officerCode } : {})}
          {...(cartCifIds !== undefined ? { cartCifIds } : {})}
          onDraftChange={handleReviewDraftChange}
          {...(onEarlyDupCheck ? { onEarlyDupCheck } : {})}
        />
      )}

    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Stage 1 — Bucket grid (extracted from EntityCategoryPicker so PdwShell stays
// scannable). Reuses the same flat-library bucket icons + 7-bucket lock.
// ---------------------------------------------------------------------------

interface BucketModel {
  group: EntityGroup;
  /** Subtypes pickable in this cart mode. */
  selectableCount: number;
  /** True when nothing in the bucket can be picked yet (all pilot/future/ICS). */
  disabled: boolean;
}

interface BucketGridProps {
  buckets: ReadonlyArray<BucketModel>;
  onPickGroup: (g: EntityGroup) => void;
}

function BucketGrid({ buckets, onPickGroup }: BucketGridProps): JSX.Element {
  return (
    <>
      <div style={{ marginBottom: 'var(--sp-2)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
          Select Entity Category
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 4 }}>
          Choose the category that best describes this customer or entity.
          Categories shown in grey are not yet available.
        </div>
      </div>
      <div
        role="list"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 'var(--sp-5)',
          marginTop: 'var(--sp-5)',
        }}
      >
        {buckets.map(({ group: g, selectableCount, disabled }) => {
          const iconUrl = entityBucketIconUrl(g.key);
          return (
            <button
              key={g.key}
              type="button"
              role="listitem"
              aria-disabled={disabled ? 'true' : 'false'}
              title={disabled ? `${g.label} is not yet available — coming soon.` : undefined}
              onClick={() => { if (!disabled) onPickGroup(g); }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-5) var(--sp-6)',
                background: disabled ? 'var(--ink-100, #f1f3f5)' : 'var(--surface-card)',
                border: '1px solid var(--ink-200)',
                borderRadius: 'var(--r-md)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                textAlign: 'left',
                minHeight: 140,
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (disabled) return;
                e.currentTarget.style.borderColor = 'var(--pcb-blue)';
                e.currentTarget.style.boxShadow = 'var(--sh-2)';
              }}
              onMouseLeave={(e) => {
                if (disabled) return;
                e.currentTarget.style.borderColor = 'var(--ink-200)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', width: '100%' }}>
                {iconUrl ? (
                  <div
                    aria-hidden
                    style={{
                      width: 48,
                      height: 48,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      filter: disabled ? 'grayscale(1)' : 'none',
                    }}
                  >
                    <img
                      src={iconUrl}
                      alt=""
                      aria-hidden
                      width={48}
                      height={48}
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                ) : (
                  <div
                    aria-hidden
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 'var(--r-sm)',
                      background: disabled ? 'var(--ink-400)' : g.color,
                      color: 'var(--on-accent)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {g.icon}
                  </div>
                )}
                <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--sp-2)' }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
                      {g.label}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>
                      {selectableCount} type{selectableCount === 1 ? '' : 's'} available
                    </div>
                  </div>
                  {disabled && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: 'var(--ink-400)',
                        color: 'var(--on-brand)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Coming soon
                    </span>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--ink-600)' }}>
                {g.description}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Stage 3 — Subtype confirm cards (non-Personal).
//
// v0.14.49 (Bill 2026-05-26, BACKLOG-0071) — per-bucket canonical SPEC
// affordances added: status-tier pills (PROD/PILOT/FUTURE) on each card,
// bucket-specific sub-header copy (SPEC Screens 06-11), bucket-specific
// info boxes (Trust Cert pathway / Dual-CIF callout / No-shared-wizard
// caveat / VA Fiduciary outlier warning), and Future-tier disabled-card
// handling (e.g., ICS in Special Purpose is not FSR-initiated; click
// surfaces an explainer instead of selection).
//
// Continue still fires `onConfirm` which dispatches OUT of the shell to
// the legacy PersonDialog for non-Personal buckets — those per-category
// dialogs migrate into the shell as Stage 9 review bodies in later
// batches (currently each lives in personDialog/{business,trust,estate,
// fiduciary,government,specialPurpose}/. The dispatch pattern preserves
// pre-v0.14.48 behaviour for those flows.
// ---------------------------------------------------------------------------

/**
 * Per-bucket Stage 3 copy table. Sub-header replaces the generic group
 * description; infoBox is an optional callout rendered above the cards;
 * tierFootnote is the bucket-specific Production/Pilot/Future breakdown
 * surfaced below the cards. All copy verbatim from SPEC Screens 06-11
 * (canonical-walkthrough/SPEC.md).
 */
interface BucketStage3Copy {
  subHeader: string;
  infoBox?: { title: string; body: string; tone?: 'info' | 'warn' };
  tierFootnote: string;
}

const BUCKET_STAGE3_COPY: Record<EntityGroup['key'], BucketStage3Copy> = {
  personal: {
    // Personal never reaches subtype-confirm (cohort gate handles it),
    // but keep an entry for type-safety.
    subHeader: 'Personal cohort — handled by Stage 2 citizenship gate, not subtype confirm.',
    tierFootnote: '',
  },
  business: {
    subHeader: 'The system will then show only the documents PCB needs for that entity type — Articles of Incorporation, Banking Resolution, EIN Letter, etc.',
    tierFootnote: 'All 4 Business subtypes are Production-tier. Bucket/subtype pick drives modal layout; tax variants within a subtype share identical Core Director fields.',
  },
  trust: {
    subHeader: 'Per-subtype CIP docs + Risk Mgmt review requirements differ. Trust Cert may be customer-provided OR auto-generated from captured Settlor + Trustee relationship rows.',
    infoBox: {
      title: 'Trust Cert pathway',
      body: 'Customer-uploaded Trust Cert accepted if provided. If NOT provided, disclosure engine auto-generates a Trust Cert from captured Settlor (SG) + current Trustee (TR) relationship rows. Trust modal Step 2 (People + Ownership) MUST require explicit Settlor + Trustee role designation.',
      tone: 'info',
    },
    tierFootnote: 'All 3 Trust & Estate subtypes are Production-tier. SNT moved to Special Purpose bucket per Bill 2026-05-11 SPEC.',
  },
  fiduciary: {
    subHeader: 'Dual-CIF protected-person model. The fiduciary manages on behalf of a separate protected-person CIF. Reporting SSN is always the protected person\'s (Field 92=S).',
    infoBox: {
      title: 'VA Fiduciary outlier — Account Type = Personal (not Fiduciary)',
      body: 'VA Fiduciary uses Account Type=Personal (Field 39=N, Call Class 7 acct / 0 CIF) — distinct from Conservator / Rep Payee / UTMA. Veteran is the account holder; the Federal Fiduciary is captured as a signer with Not-Authorized flag.',
      tone: 'warn',
    },
    tierFootnote: 'All 4 Fiduciary subtypes are Pilot-tier at launch — visible in Sandbox/Pilot, hidden from Production picker until Risk Mgmt promotes.',
  },
  'special-purpose': {
    subHeader: 'Specialty / non-standard fiduciary + Medicaid + USDA Grant subtypes. Each is structurally distinct — there is NO shared "Special Purpose wizard"; they share only bucket placement.',
    infoBox: {
      title: 'ICS (IntraFi Cash Sweep) is NOT FSR-initiated',
      body: 'ICS is loaded by Finance via Core Director Add Account tab. ICS does NOT use Deposit Pro and cannot be selected from this picker. Contact Finance via email if a customer requests an ICS account.',
      tone: 'warn',
    },
    tierFootnote: 'Pilot: QIT, SNT. Future: IOLTA, USDA Grant, Medical Set-Aside, ICS. SNT moved here from Trust bucket per Bill 2026-05-11 SPEC.',
  },
  charitable: {
    subHeader: 'Non-Profit Corp + Unincorporated Association are Business-cohort siblings (Call Class 21). Donation Account is bifurcated — Short-Term routes Personal-with-title overlay; Long-Term routes Trust.',
    tierFootnote: 'Production: Non-Profit Corp. Future: Unincorp Association, Donation Account. Donation Long-Term inherits Trust tier when routed.',
  },
  government: {
    subHeader: 'Public Funds (CDD-exempt government) and Political Committee (MO Ethics-regulated) have distinct Call Class assignments + locked field defaults.',
    tierFootnote: '2 Pilot-tier subtypes. Both have BO Form variant differences (Public Funds is NONE_EXEMPT; Political Committee is BO + Controller full).',
  },
};

interface SubtypeConfirmProps {
  group: EntityGroup;
  statusByType: Map<string, 'Production' | 'UAT' | 'Future'>;
  cartMode?: string;
  selectedType: CustomerType | null;
  onSelectType: (t: CustomerType) => void;
  onConfirm: () => void;
  onBack: () => void;
}

function SubtypeConfirm({
  group,
  statusByType,
  cartMode,
  selectedType,
  onSelectType,
  onConfirm,
  onBack,
}: SubtypeConfirmProps): JSX.Element {
  const copy = BUCKET_STAGE3_COPY[group.key];
  const subtypeDescription =
    selectedType ? group.types.find((t) => t.type === selectedType)?.description ?? '' : '';
  // Inline explainer shown when the FSR clicks a not-FSR-initiated card
  // (only ICS_Business / ICS_Personal today). State lives on the parent
  // shell for the modal-level affordance; this is a transient card-level
  // banner so a local useState here is fine.
  const [notFsrExplainerType, setNotFsrExplainerType] = useState<CustomerType | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-4)',
          padding: 'var(--sp-4) var(--sp-5)',
          background: group.bg,
          borderRadius: 'var(--r-sm)',
          border: '1px solid var(--ink-200)',
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to category list"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 16,
            color: 'var(--ink-700)',
            padding: 4,
          }}
        >
          ←
        </button>
        <div
          aria-hidden
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--r-sm)',
            background: group.color,
            color: 'var(--on-accent)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 16,
          }}
        >
          {group.icon}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>{group.label}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-700)', lineHeight: 1.4 }}>
            {copy.subHeader}
          </div>
        </div>
      </div>

      {copy.infoBox && (
        <div
          role="note"
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            borderRadius: 'var(--r-sm)',
            background: copy.infoBox.tone === 'warn'
              ? 'var(--pcb-amber-bg, #fef9e7)'
              : 'var(--pcb-blue-bg, #d9edf7)',
            border: `1px solid ${
              copy.infoBox.tone === 'warn'
                ? 'var(--pcb-amber-text, #b58400)'
                : 'var(--pcb-blue, #2968ad)'
            }`,
            fontSize: 12,
            lineHeight: 1.5,
            color: 'var(--ink-800)',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--pcb-blue-dark)' }}>
            {copy.infoBox.tone === 'warn' ? '⚠ ' : 'ℹ '}
            {copy.infoBox.title}
          </div>
          <div>{copy.infoBox.body}</div>
        </div>
      )}

      <div
        role="radiogroup"
        aria-label={`${group.label} subtypes`}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 'var(--sp-4)',
        }}
      >
        {group.types
          .map((t) => {
            const isSelected = selectedType === t.type;
            const tier = statusByType.get(t.type) ?? 'Production';
            const isNotFsrInitiated = NOT_FSR_INITIATED.has(t.type);
            const admissible = allowedForMode(statusByType.get(t.type), cartMode);
            // Scaffold-and-disable (Bill 2026-06-13): EVERY subtype renders.
            // A card is disabled (greyed, non-selectable) when it isn't
            // admissible in this cart mode — i.e. UAT/"Pilot" or Future tier in
            // a Production cart — or it's a Finance-only ICS flow. This keeps
            // the full taxonomy visible (IOLTA, USDA Grant, Medical Set-Aside,
            // Unincorporated Association, Donation, Political Committee, the
            // fiduciaries, …) so the bucket descriptions aren't confusing.
            // NOT_FSR_INITIATED cards flip to a transient explainer on click.
            const isDisabled = !admissible || isNotFsrInitiated;
            const tierPill = (
              <span
                aria-label={`Status tier: ${tier}`}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  padding: '2px 8px',
                  borderRadius: 999,
                  background:
                    tier === 'Production'
                      ? 'var(--ok-fill)'
                      : tier === 'UAT'
                        ? 'var(--warn-fill)'
                        : 'var(--ink-400)',
                  color: 'var(--on-status-fill)',
                  textTransform: 'uppercase',
                }}
              >
                {tier === 'Production' ? 'PROD' : tier === 'UAT' ? 'PILOT' : 'FUTURE'}
              </span>
            );
            return (
              <button
                key={t.type}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-disabled={isDisabled}
                onClick={() => {
                  if (isNotFsrInitiated) {
                    setNotFsrExplainerType(t.type);
                    return;
                  }
                  if (isDisabled) return;
                  onSelectType(t.type);
                }}
                onDoubleClick={() => {
                  if (isDisabled) return;
                  onSelectType(t.type);
                  onConfirm();
                }}
                title={
                  isNotFsrInitiated
                    ? 'NOT FSR-initiated — Finance loads this account type via Core Director directly.'
                    : !admissible
                      ? `${tier === 'UAT' ? 'Pilot' : 'Future'}-tier — not yet available in this cart mode.`
                      : undefined
                }
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 'var(--sp-3)',
                  padding: 'var(--sp-4) var(--sp-5)',
                  background: isDisabled
                    ? 'var(--ink-100, #f1f3f5)'
                    : isSelected
                      ? 'var(--pcb-blue-bg, #d9edf7)'
                      : 'var(--surface-card)',
                  border: isSelected && !isDisabled
                    ? '2px solid var(--pcb-blue, #2968ad)'
                    : '1px solid var(--ink-200)',
                  borderRadius: 'var(--r-md)',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.6 : 1,
                  textAlign: 'left',
                  minHeight: 132,
                  transition: 'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
                  boxShadow: isSelected && !isDisabled ? 'var(--sh-2)' : 'none',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected && !isDisabled) {
                    e.currentTarget.style.borderColor = 'var(--pcb-blue, #2968ad)';
                    e.currentTarget.style.boxShadow = 'var(--sh-1, 0 1px 2px rgba(0,0,0,0.06))';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected && !isDisabled) {
                    e.currentTarget.style.borderColor = 'var(--ink-200)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', width: '100%' }}>
                  <div
                    aria-hidden
                    style={{
                      width: 48,
                      height: 48,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <EntitySubtypeIcon customerType={t.type} size={48} />
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 'var(--sp-2)',
                      }}
                    >
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
                        {t.label}
                      </span>
                      {tierPill}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--ink-600)' }}>
                  {t.description}
                </div>
              </button>
            );
          })}
      </div>

      {notFsrExplainerType && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--pcb-amber-bg, #fef9e7)',
            border: '1px solid var(--pcb-amber-text, #b58400)',
            fontSize: 12,
            color: 'var(--ink-800)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 'var(--sp-4)',
          }}
        >
          <div>
            <strong>⚠ NOT FSR-initiated.</strong>{' '}
            ICS (IntraFi Cash Sweep) is loaded by Finance via Core Director Add Account tab.
            ICS does NOT use Deposit Pro. Contact Finance via email if a customer requests
            an ICS account.
          </div>
          <button
            type="button"
            onClick={() => setNotFsrExplainerType(null)}
            aria-label="Dismiss explainer"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              color: 'var(--ink-700)',
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{ fontSize: 12, color: 'var(--ink-600)', lineHeight: 1.5 }}>
        {selectedType ? (
          <>
            <strong>{group.types.find((t) => t.type === selectedType)?.label}</strong>
            {' '}selected. Click <strong>Continue →</strong> to capture identity, ID documents,
            and CIP. Roles per product (Primary / Joint / Beneficiary / etc.) are assigned on
            the Products step.
            {subtypeDescription && (
              <span style={{ display: 'block', marginTop: 6 }}>{subtypeDescription}</span>
            )}
          </>
        ) : (
          <>Select a subtype above to continue.</>
        )}
      </div>

      {copy.tierFootnote && (
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-500)',
            lineHeight: 1.5,
            paddingTop: 'var(--sp-3)',
            borderTop: '1px dashed var(--ink-200)',
          }}
        >
          {copy.tierFootnote}
        </div>
      )}
    </div>
  );
}

export default PdwShell;
